import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { useFamily, ROLES } from '../contexts/FamilyContext';
import { useTheme } from '../contexts/ThemeContext';
import { useCurrency } from '../contexts/CurrencyContext';
import { 
  Users, Plus, UserPlus, Crown, Shield, User, MoreVertical, 
  Trash2, Mail, Check, X, ArrowLeft, Settings, DollarSign,
  AlertTriangle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { toast } from 'sonner';
import { format } from 'date-fns';

const INCOME_CATEGORIES = [
  { label: '💼 Salary/Wages', value: 'Salary/Wages', icon: '💼' },
  { label: '🏠 Rental Income', value: 'Rental Income', icon: '🏠' },
  { label: '🤝 Shared Contribution', value: 'Shared Contribution', icon: '🤝' },
  { label: '📈 Investment Returns', value: 'Investment Returns', icon: '📈' },
  { label: '💻 Freelance/Side Income', value: 'Freelance/Side Income', icon: '💻' },
  { label: '🏪 Business Income', value: 'Business Income', icon: '🏪' },
  { label: '🎁 Gifts/Allowances', value: 'Gifts/Allowances', icon: '🎁' },
  { label: '💰 Tax Refunds', value: 'Tax Refunds', icon: '💰' },
  { label: '📥 Other Income', value: 'Other Income', icon: '📥' }
];

const EXPENSE_CATEGORIES = [
  { label: '🏡 Rent/Mortgage', value: 'Rent/Mortgage', icon: '🏡' },
  { label: '💡 Utilities', value: 'Utilities', icon: '💡' },
  { label: '🛒 Groceries', value: 'Groceries', icon: '🛒' },
  { label: '🧹 Household Items', value: 'Household Items', icon: '🧹' },
  { label: '📶 Internet/Cable', value: 'Internet/Cable', icon: '📶' },
  { label: '🛡️ Insurance', value: 'Insurance', icon: '🛡️' },
  { label: '🏥 Medical/Healthcare', value: 'Medical/Healthcare', icon: '🏥' },
  { label: '👶 Children/Education', value: 'Children/Education', icon: '👶' },
  { label: '🚗 Transportation', value: 'Transportation', icon: '🚗' },
  { label: '🍽️ Dining Out', value: 'Dining Out', icon: '🍽️' },
  { label: '🎬 Entertainment', value: 'Entertainment', icon: '🎬' },
  { label: '🛍️ Shopping', value: 'Shopping', icon: '🛍️' },
  { label: '📱 Subscriptions', value: 'Subscriptions', icon: '📱' },
  { label: '🔧 Maintenance/Repairs', value: 'Maintenance/Repairs', icon: '🔧' },
  { label: '🐖 Savings/Investments', value: 'Savings/Investments', icon: '🐖' },
  { label: '📤 Other Expenses', value: 'Other Expenses', icon: '📤' }
];

const FamilyBudget = () => {
  const navigate = useNavigate();
  const { 
    family, members, pendingInvites, sharedTransactions, sharedBudgets,
    familyNotifications,
    loading, userRole, isOwner, isAdmin, isMember,
    createFamily, inviteMember, acceptInvite, declineInvite,
    removeMember, updateMemberRole, addSharedTransaction, setSharedBudget,
    leaveFamily, deleteFamily, renameFamily
  } = useFamily();
  const { theme, isDark } = useTheme();
  const { formatCurrency, displayCurrency, getCurrencyInfo } = useCurrency();
  const currencyInfo = getCurrencyInfo(displayCurrency);

  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [showBudgetDialog, setShowBudgetDialog] = useState(false);
  const [showTransactionDialog, setShowTransactionDialog] = useState(false);
  const [showLeaveDialog, setShowLeaveDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showRemoveDialog, setShowRemoveDialog] = useState(false);
  const [showRenameDialog, setShowRenameDialog] = useState(false);

  const [familyName, setFamilyName] = useState('');
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState(ROLES.MEMBER);
  const [removingMember, setRemovingMember] = useState(null);
  const [budgetForm, setBudgetForm] = useState({ category: '', amount: '' });
  const [transactionForm, setTransactionForm] = useState({
    type: 'expense',
    amount: '',
    category: 'Groceries',
    description: '',
    date: format(new Date(), 'yyyy-MM-dd')
  });

  // Calculate spending by category
  const categorySpending = useMemo(() => {
    const spending = {};
    sharedTransactions
      .filter(t => t.type === 'expense')
      .forEach(t => {
        spending[t.category] = (spending[t.category] || 0) + t.amount;
      });
    return spending;
  }, [sharedTransactions]);

  // Calculate spending by member
  const memberSpending = useMemo(() => {
    const spending = {};
    sharedTransactions.forEach(t => {
      if (!spending[t.userId]) {
        spending[t.userId] = { income: 0, expense: 0 };
      }
      if (t.type === 'income') {
        spending[t.userId].income += t.amount;
      } else {
        spending[t.userId].expense += t.amount;
      }
    });
    return spending;
  }, [sharedTransactions]);

  const handleCreateFamily = async () => {
    if (!familyName.trim()) {
      toast.error('Please enter a family name');
      return;
    }
    try {
      await createFamily(familyName.trim());
      toast.success('Family created successfully!');
      setShowCreateDialog(false);
      setFamilyName('');
    } catch (error) {
      console.error('Create family error:', error);
      toast.error('Failed to create family');
    }
  };

  const handleRenameFamily = async () => {
    if (!familyName.trim()) {
      toast.error('Please enter a family name');
      return;
    }
    try {
      await renameFamily(familyName.trim());
      toast.success('Family name updated!');
      setShowRenameDialog(false);
    } catch (error) {
      toast.error(error.message || 'Failed to rename family');
    }
  };

  const handleInviteMember = async () => {
    if (!inviteEmail.trim()) {
      toast.error('Please enter an email');
      return;
    }
    try {
      await inviteMember(inviteEmail.trim(), inviteRole);
      toast.success(`Invitation created for ${inviteEmail}! They will see it when they log in with this Google account.`);
      setShowInviteDialog(false);
      setInviteEmail('');
      setInviteRole(ROLES.MEMBER);
    } catch (error) {
      toast.error(error.message || 'Failed to send invite');
    }
  };

  const handleSetBudget = async () => {
    if (!budgetForm.category || !budgetForm.amount) {
      toast.error('Please fill all fields');
      return;
    }
    try {
      await setSharedBudget(budgetForm.category, parseFloat(budgetForm.amount));
      toast.success('Budget set!');
      setShowBudgetDialog(false);
      setBudgetForm({ category: '', amount: '' });
    } catch (error) {
      toast.error('Failed to set budget');
    }
  };

  const handleAddTransaction = async () => {
    if (!transactionForm.amount) {
      toast.error('Please enter an amount');
      return;
    }
    try {
      await addSharedTransaction({
        ...transactionForm,
        amount: parseFloat(transactionForm.amount),
        date: new Date(transactionForm.date)
      });
      toast.success('Transaction added!');
      setShowTransactionDialog(false);
      setTransactionForm({
        type: 'expense',
        amount: '',
        category: 'Food & Dining',
        description: '',
        date: format(new Date(), 'yyyy-MM-dd')
      });
    } catch (error) {
      toast.error('Failed to add transaction');
    }
  };

  const handleRemoveMember = async () => {
    if (!removingMember) return;
    try {
      await removeMember(removingMember.id);
      toast.success('Member removed');
      setShowRemoveDialog(false);
      setRemovingMember(null);
    } catch (error) {
      toast.error('Failed to remove member');
    }
  };

  const handleLeaveFamily = async () => {
    try {
      await leaveFamily();
      toast.success('You left the family');
      setShowLeaveDialog(false);
    } catch (error) {
      toast.error(error.message || 'Failed to leave family');
    }
  };

  const handleDeleteFamily = async () => {
    try {
      await deleteFamily();
      toast.success('Family deleted');
      setShowDeleteDialog(false);
    } catch (error) {
      toast.error('Failed to delete family');
    }
  };

  const getRoleIcon = (role) => {
    if (role === ROLES.OWNER) return <Crown className="w-4 h-4 text-yellow-500" />;
    if (role === ROLES.ADMIN) return <Shield className="w-4 h-4 text-blue-500" />;
    return <User className="w-4 h-4" style={{ color: theme.textSecondary }} />;
  };

  const getCategoryEmoji = (category) => {
    const allCategories = [...INCOME_CATEGORIES, ...EXPENSE_CATEGORIES];
    const found = allCategories.find(c => c.value === category);
    return found?.icon || '📋';
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <p style={{ color: theme.textSecondary }}>Loading...</p>
        </div>
      </Layout>
    );
  }

  // Show pending invites if user has any
  if (!family && pendingInvites.length > 0) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto">
          <h1 className="text-2xl font-bold mb-6" style={{ color: theme.text }}>Family Invitations</h1>
          <div className="space-y-4">
            {pendingInvites.map(invite => (
              <div
                key={invite.id}
                className="p-4 rounded-xl"
                style={{ backgroundColor: theme.cardBackground, boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)' }}
              >
                <p className="font-semibold mb-1" style={{ color: theme.text }}>
                  {invite.invitedByName} invited you to join
                </p>
                <p className="text-lg font-bold mb-2" style={{ color: theme.accent }}>
                  {invite.familyName}
                </p>
                <p className="text-sm mb-4" style={{ color: theme.textSecondary }}>
                  Role: {invite.role.charAt(0).toUpperCase() + invite.role.slice(1)}
                </p>
                <div className="flex gap-2">
                  <Button
                    onClick={() => acceptInvite(invite.id)}
                    style={{ backgroundColor: '#4CAF50', color: 'white' }}
                  >
                    <Check className="w-4 h-4 mr-2" /> Accept
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => declineInvite(invite.id)}
                    style={{ color: theme.accent }}
                  >
                    <X className="w-4 h-4 mr-2" /> Decline
                  </Button>
                </div>
              </div>
            ))}
          </div>
          
          <div className="mt-8 text-center">
            <p className="text-sm mb-4" style={{ color: theme.textSecondary }}>
              Or create your own family
            </p>
            <Button onClick={() => setShowCreateDialog(true)} style={{ backgroundColor: theme.accent, color: 'white' }}>
              <Plus className="w-4 h-4 mr-2" /> Create Family
            </Button>
          </div>
        </div>

        {/* Create Family Dialog */}
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogContent style={{ backgroundColor: theme.cardBackground }}>
            <DialogHeader>
              <DialogTitle style={{ color: theme.text }}>Create Family</DialogTitle>
            </DialogHeader>
            <div className="py-4">
              <Label style={{ color: theme.textSecondary }}>Family Name</Label>
              <Input
                value={familyName}
                onChange={(e) => setFamilyName(e.target.value)}
                placeholder="e.g., The Smiths"
                className="mt-1"
                style={{ backgroundColor: isDark ? '#3A3A3C' : '#FFFFFF', color: theme.text }}
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
              <Button onClick={handleCreateFamily} style={{ backgroundColor: theme.accent, color: 'white' }}>
                Create
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </Layout>
    );
  }

  // Show create family option if not in a family
  if (!family) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto">
          <div 
            className="text-center py-16 rounded-xl"
            style={{ backgroundColor: theme.cardBackground, boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)' }}
          >
            <div 
              className="inline-flex items-center justify-center w-20 h-20 rounded-full mb-4"
              style={{ backgroundColor: isDark ? '#2C2C2E' : '#F8F9FA' }}
            >
              <Users className="w-10 h-10" style={{ color: theme.accent }} />
            </div>
            <h2 className="text-xl font-bold mb-2" style={{ color: theme.text }}>
              Family / Shared Mode
            </h2>
            <p className="text-sm mb-6 px-8" style={{ color: theme.textSecondary }}>
              Create a family group to share budgets and track expenses together with family members or roommates.
            </p>
            <Button
              onClick={() => setShowCreateDialog(true)}
              className="mx-auto"
              style={{ backgroundColor: theme.accent, color: 'white' }}
            >
              <Plus className="w-4 h-4 mr-2" /> Create Family Group
            </Button>
          </div>
        </div>

        {/* Create Family Dialog */}
        <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
          <DialogContent style={{ backgroundColor: theme.cardBackground }}>
            <DialogHeader>
              <DialogTitle style={{ color: theme.text }}>Create Family</DialogTitle>
            </DialogHeader>
            <div className="py-4">
              <Label style={{ color: theme.textSecondary }}>Family Name</Label>
              <Input
                value={familyName}
                onChange={(e) => setFamilyName(e.target.value)}
                placeholder="e.g., The Smiths"
                className="mt-1"
                style={{ backgroundColor: isDark ? '#3A3A3C' : '#FFFFFF', color: theme.text }}
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
              <Button onClick={handleCreateFamily} style={{ backgroundColor: theme.accent, color: 'white' }}>
                Create
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </Layout>
    );
  }

  // Main family view
  return (
    <Layout>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <h1 className="text-2xl font-bold" style={{ color: theme.text }}>{family.name}</h1>
              {isOwner && (
                <button
                  onClick={() => {
                    setFamilyName(family.name || '');
                    setShowRenameDialog(true);
                  }}
                  className="p-1 rounded-md text-xs border transition-colors"
                  style={{ borderColor: theme.divider, color: theme.textSecondary }}
                >
                  Edit
                </button>
              )}
            </div>
            <p className="text-sm" style={{ color: theme.textSecondary }}>
              You are {userRole} • {members.length} member{members.length !== 1 ? 's' : ''}
            </p>
          </div>
          <div className="flex gap-2">
            {isAdmin && (
              <Button
                onClick={() => setShowInviteDialog(true)}
                variant="outline"
                style={{ borderColor: theme.divider, color: theme.text }}
              >
                <UserPlus className="w-4 h-4 mr-2" /> Invite
              </Button>
            )}
            <Button
              onClick={() => setShowTransactionDialog(true)}
              style={{ backgroundColor: theme.accent, color: 'white' }}
            >
              <Plus className="w-4 h-4 mr-2" /> Add Expense
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="p-4 rounded-xl" style={{ backgroundColor: theme.cardBackground, boxShadow: isDark ? '0 2px 8px rgba(0, 0, 0, 0.3)' : '0 2px 8px rgba(0, 0, 0, 0.08)' }}>
            <p className="text-sm" style={{ color: theme.textSecondary }}>Total Income</p>
            <p className="text-2xl font-bold" style={{ color: theme.income }}>
              {formatCurrency(sharedTransactions.filter(t => t.type === 'income').reduce((s, t) => s + t.amount, 0))}
            </p>
          </div>
          <div className="p-4 rounded-xl" style={{ backgroundColor: theme.cardBackground, boxShadow: isDark ? '0 2px 8px rgba(0, 0, 0, 0.3)' : '0 2px 8px rgba(0, 0, 0, 0.08)' }}>
            <p className="text-sm" style={{ color: theme.textSecondary }}>Total Expenses</p>
            <p className="text-2xl font-bold" style={{ color: theme.accent }}>
              {formatCurrency(sharedTransactions.filter(t => t.type === 'expense').reduce((s, t) => s + t.amount, 0))}
            </p>
          </div>
          <div className="p-4 rounded-xl" style={{ backgroundColor: theme.cardBackground, boxShadow: isDark ? '0 2px 8px rgba(0, 0, 0, 0.3)' : '0 2px 8px rgba(0, 0, 0, 0.08)' }}>
            <p className="text-sm" style={{ color: theme.textSecondary }}>Balance</p>
            <p className="text-2xl font-bold" style={{ color: theme.text }}>
              {formatCurrency(
                sharedTransactions.filter(t => t.type === 'income').reduce((s, t) => s + t.amount, 0) -
                sharedTransactions.filter(t => t.type === 'expense').reduce((s, t) => s + t.amount, 0)
              )}
            </p>
          </div>
        </div>

        {/* Family Members Section - Enhanced */}
        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-3">
              <h2 className="text-lg font-semibold" style={{ color: theme.text }}>Family Members</h2>
              <span 
                className="px-3 py-1 text-xs font-semibold rounded-full"
                style={{ backgroundColor: theme.accent, color: 'white' }}
              >
                {members.length} member{members.length !== 1 ? 's' : ''}
              </span>
            </div>
            {isAdmin && (
              <Button 
                onClick={() => setShowInviteDialog(true)} 
                variant="outline" 
                size="sm"
                style={{ borderColor: theme.divider, color: theme.text }}
              >
                <UserPlus className="w-4 h-4 mr-1" /> Invite Member
              </Button>
            )}
          </div>
          <div className="rounded-xl overflow-hidden" style={{ backgroundColor: theme.cardBackground, boxShadow: isDark ? '0 2px 8px rgba(0, 0, 0, 0.3)' : '0 2px 8px rgba(0, 0, 0, 0.08)', border: `1px solid ${theme.divider}` }}>
            {members.map((member, index) => {
              const spending = memberSpending[member.id] || { income: 0, expense: 0 };
              return (
                <div
                  key={member.id}
                  className="p-4 flex items-center justify-between transition-colors"
                  style={{ 
                    borderBottom: index < members.length - 1 ? `1px solid ${theme.divider}` : 'none',
                    backgroundColor: 'transparent'
                  }}
                >
                  <div className="flex items-center gap-4">
                    <Avatar className="w-12 h-12 ring-2" style={{ ringColor: theme.divider }}>
                      <AvatarImage src={member.photoURL} />
                      <AvatarFallback style={{ backgroundColor: isDark ? '#252C38' : '#E9ECEF', color: theme.text }}>
                        {member.displayName?.charAt(0).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <p className="font-semibold" style={{ color: theme.text }}>{member.displayName}</p>
                        {/* Role Badge */}
                        <span 
                          className="px-2 py-0.5 text-xs font-medium rounded-full flex items-center gap-1"
                          style={{ 
                            backgroundColor: member.role === ROLES.OWNER 
                              ? (isDark ? '#3D3520' : '#FEF3C7')
                              : member.role === ROLES.ADMIN 
                                ? (isDark ? '#1F2937' : '#DBEAFE')
                                : (isDark ? '#252C38' : '#F3F4F6'),
                            color: member.role === ROLES.OWNER 
                              ? '#D97706'
                              : member.role === ROLES.ADMIN 
                                ? '#2563EB'
                                : theme.textSecondary
                          }}
                        >
                          {getRoleIcon(member.role)}
                          <span className="ml-0.5">{member.role.charAt(0).toUpperCase() + member.role.slice(1)}</span>
                        </span>
                      </div>
                      <p className="text-sm" style={{ color: theme.textSecondary }}>{member.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right">
                      <p className="text-sm font-medium" style={{ color: theme.income }}>+{formatCurrency(spending.income)}</p>
                      <p className="text-sm font-medium" style={{ color: theme.accent }}>-{formatCurrency(spending.expense)}</p>
                    </div>
                    {isOwner && member.role !== ROLES.OWNER && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button className="p-2 rounded-lg transition-colors" style={{ backgroundColor: 'transparent' }}>
                            <MoreVertical className="w-4 h-4" style={{ color: theme.textSecondary }} />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent style={{ backgroundColor: theme.cardBackground, borderColor: theme.divider }}>
                          <DropdownMenuItem 
                            onClick={() => updateMemberRole(member.id, member.role === ROLES.ADMIN ? ROLES.MEMBER : ROLES.ADMIN)}
                            style={{ color: theme.text }}
                          >
                            <Shield className="w-4 h-4 mr-2" />
                            {member.role === ROLES.ADMIN ? 'Remove Admin' : 'Make Admin'}
                          </DropdownMenuItem>
                          <DropdownMenuItem 
                            onClick={() => { setRemovingMember(member); setShowRemoveDialog(true); }}
                            style={{ color: theme.accent }}
                          >
                            <Trash2 className="w-4 h-4 mr-2" /> Remove
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </div>
                </div>
              );
            })}
            
            {/* Pending Invites */}
            {pendingInvites.length > 0 && (
              <>
                <div className="px-4 py-2" style={{ backgroundColor: isDark ? '#252C38' : '#F8F9FA' }}>
                  <p className="text-xs font-medium uppercase tracking-wide" style={{ color: theme.textSecondary }}>Pending Invites</p>
                </div>
                {pendingInvites.map(invite => (
                  <div key={invite.id} className="p-4 flex items-center justify-between opacity-70">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: isDark ? '#252C38' : '#E9ECEF' }}>
                        <Mail className="w-5 h-5" style={{ color: theme.textSecondary }} />
                      </div>
                      <div>
                        <p className="font-medium" style={{ color: theme.text }}>{invite.email}</p>
                        <p className="text-xs" style={{ color: theme.textSecondary }}>
                          Pending • {invite.role.charAt(0).toUpperCase() + invite.role.slice(1)}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
        </div>

        {/* Budget Overview */}
        {isAdmin && (
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-semibold" style={{ color: theme.text }}>Shared Budgets</h2>
              <Button variant="outline" size="sm" onClick={() => setShowBudgetDialog(true)} style={{ borderColor: theme.divider, color: theme.text }}>
                <Plus className="w-4 h-4 mr-1" /> Set Budget
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {Object.entries(sharedBudgets).map(([category, budget]) => {
                const spent = categorySpending[category] || 0;
                const percentage = Math.min((spent / budget) * 100, 100);
                const isOver = spent > budget;
                
                return (
                  <div
                    key={category}
                    className="p-4 rounded-xl"
                    style={{ backgroundColor: theme.cardBackground, boxShadow: isDark ? '0 2px 8px rgba(0, 0, 0, 0.3)' : '0 2px 8px rgba(0, 0, 0, 0.08)', border: `1px solid ${theme.divider}` }}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <p className="font-medium" style={{ color: theme.text }}>{category}</p>
                      {isOver && <AlertTriangle className="w-4 h-4" style={{ color: theme.accent }} />}
                    </div>
                    <div className="flex justify-between text-sm mb-2">
                      <span style={{ color: theme.textSecondary }}>
                        {formatCurrency(spent)} / {formatCurrency(budget)}
                      </span>
                      <span style={{ color: isOver ? theme.accent : theme.income }}>
                        {percentage.toFixed(0)}%
                      </span>
                    </div>
                    <div className="h-2 rounded-full overflow-hidden" style={{ backgroundColor: isDark ? '#252C38' : '#E9ECEF' }}>
                      <div 
                        className="h-full rounded-full transition-all"
                        style={{ 
                          width: `${percentage}%`, 
                          backgroundColor: isOver ? theme.accent : theme.income 
                        }}
                      />
                    </div>
                  </div>
                );
              })}
              {Object.keys(sharedBudgets).length === 0 && (
                <div className="col-span-2 text-center py-8 rounded-xl" style={{ backgroundColor: theme.cardBackground, border: `1px solid ${theme.divider}` }}>
                  <p style={{ color: theme.textSecondary }}>No budgets set yet</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Recent Transactions */}
        <div className="mb-6">
          <h2 className="text-lg font-semibold mb-3" style={{ color: theme.text }}>Recent Shared Transactions</h2>
          <div className="rounded-xl overflow-hidden" style={{ backgroundColor: theme.cardBackground, boxShadow: isDark ? '0 2px 8px rgba(0, 0, 0, 0.3)' : '0 2px 8px rgba(0, 0, 0, 0.08)', border: `1px solid ${theme.divider}` }}>
            {sharedTransactions.slice(0, 10).map((trans, index) => (
              <div
                key={trans.id}
                className="p-4 flex items-center justify-between"
                style={{ borderBottom: index < Math.min(sharedTransactions.length, 10) - 1 ? `1px solid ${theme.divider}` : 'none' }}
              >
                <div className="flex items-center gap-3">
                  <div 
                    className="w-10 h-10 rounded-full flex items-center justify-center text-lg"
                    style={{ backgroundColor: isDark ? '#252C38' : '#F8F9FA' }}
                  >
                    {getCategoryEmoji(trans.category)}
                  </div>
                  <div>
                    <p className="font-medium text-sm" style={{ color: theme.text }}>{trans.category}</p>
                    <p className="text-xs" style={{ color: theme.textSecondary }}>
                      {trans.userName} • {format(trans.date, 'MMM d')}
                    </p>
                  </div>
                </div>
                <p 
                  className="font-bold"
                  style={{ color: trans.type === 'income' ? theme.income : theme.accent }}
                >
                  {trans.type === 'income' ? '+' : '-'}{formatCurrency(trans.amount)}
                </p>
              </div>
            ))}
            {sharedTransactions.length === 0 && (
              <div className="text-center py-8">
                <p style={{ color: theme.textSecondary }}>No shared transactions yet</p>
              </div>
            )}
          </div>
        </div>

        {/* Actions */}
        <div className="space-y-3">
          {!isOwner && (
            <Button
              onClick={() => setShowLeaveDialog(true)}
              variant="outline"
              className="w-full"
              style={{ borderColor: theme.accent, color: theme.accent }}
            >
              Leave Family
            </Button>
          )}
          {isOwner && (
            <Button
              onClick={() => setShowDeleteDialog(true)}
              variant="outline"
              className="w-full"
              style={{ borderColor: '#DC3545', color: '#DC3545' }}
            >
              Delete Family
            </Button>
          )}
        </div>

        {/* Rename Family Dialog (owner only) */}
        <Dialog open={showRenameDialog} onOpenChange={setShowRenameDialog}>
          <DialogContent style={{ backgroundColor: theme.cardBackground }}>
            <DialogHeader>
              <DialogTitle style={{ color: theme.text }}>Rename Family</DialogTitle>
            </DialogHeader>
            <div className="py-4 space-y-2">
              <Label style={{ color: theme.textSecondary }}>Family Name</Label>
              <Input
                value={familyName}
                onChange={(e) => setFamilyName(e.target.value)}
                placeholder="e.g., The Smiths"
                className="mt-1"
                style={{ backgroundColor: isDark ? '#3A3A3C' : '#FFFFFF', color: theme.text }}
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowRenameDialog(false)}>
                Cancel
              </Button>
              <Button onClick={handleRenameFamily} style={{ backgroundColor: theme.accent, color: 'white' }}>
                Save
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Invite Dialog */}
        <Dialog open={showInviteDialog} onOpenChange={setShowInviteDialog}>
          <DialogContent style={{ backgroundColor: theme.cardBackground, borderColor: theme.divider }}>
            <DialogHeader>
              <DialogTitle style={{ color: theme.text }}>Invite Member</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div 
                className="p-3 rounded-lg text-sm"
                style={{ backgroundColor: isDark ? '#252C38' : '#FEF3C7', color: isDark ? '#FFB74D' : '#92400E' }}
              >
                <p>The invited person will see the invitation when they sign in to Spend Easy with their Google account (same email address).</p>
              </div>
              <div>
                <Label style={{ color: theme.textSecondary }}>Google Account Email</Label>
                <Input
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  placeholder="member@gmail.com"
                  className="mt-1"
                  style={{ backgroundColor: isDark ? theme.inputBackground : '#FFFFFF', color: theme.text, borderColor: theme.divider }}
                />
              </div>
              <div>
                <Label style={{ color: theme.textSecondary }}>Role</Label>
                <Select value={inviteRole} onValueChange={setInviteRole}>
                  <SelectTrigger className="mt-1" style={{ backgroundColor: isDark ? theme.inputBackground : '#FFFFFF', color: theme.text, borderColor: theme.divider }}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent style={{ backgroundColor: theme.cardBackground, borderColor: theme.divider }}>
                    <SelectItem value={ROLES.MEMBER}>Member (can add transactions)</SelectItem>
                    <SelectItem value={ROLES.ADMIN}>Admin (can manage budgets)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowInviteDialog(false)} style={{ borderColor: theme.divider, color: theme.text }}>Cancel</Button>
              <Button onClick={handleInviteMember} style={{ backgroundColor: theme.accent, color: 'white' }}>
                Create Invite
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Budget Dialog */}
        <Dialog open={showBudgetDialog} onOpenChange={setShowBudgetDialog}>
          <DialogContent style={{ backgroundColor: theme.cardBackground, borderColor: theme.divider }}>
            <DialogHeader>
              <DialogTitle style={{ color: theme.text }}>Set Shared Budget</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label style={{ color: theme.textSecondary }}>Category</Label>
                <Select value={budgetForm.category} onValueChange={(v) => setBudgetForm({ ...budgetForm, category: v })}>
                  <SelectTrigger className="mt-1" style={{ backgroundColor: isDark ? theme.inputBackground : '#FFFFFF', color: theme.text, borderColor: theme.divider }}>
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent style={{ backgroundColor: theme.cardBackground, borderColor: theme.divider }}>
                    {EXPENSE_CATEGORIES.map(cat => (
                      <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label style={{ color: theme.textSecondary }}>Budget Amount ({currencyInfo.symbol})</Label>
                <Input
                  type="number"
                  value={budgetForm.amount}
                  onChange={(e) => setBudgetForm({ ...budgetForm, amount: e.target.value })}
                  placeholder="10000"
                  className="mt-1"
                  style={{ backgroundColor: isDark ? theme.inputBackground : '#FFFFFF', color: theme.text, borderColor: theme.divider }}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowBudgetDialog(false)} style={{ borderColor: theme.divider, color: theme.text }}>Cancel</Button>
              <Button onClick={handleSetBudget} style={{ backgroundColor: theme.accent, color: 'white' }}>
                Set Budget
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Add Transaction Dialog */}
        <Dialog open={showTransactionDialog} onOpenChange={setShowTransactionDialog}>
          <DialogContent style={{ backgroundColor: theme.cardBackground, borderColor: theme.divider }}>
            <DialogHeader>
              <DialogTitle style={{ color: theme.text }}>Add Shared Transaction</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="flex gap-2">
                <Button
                  onClick={() => setTransactionForm({ ...transactionForm, type: 'expense' })}
                  variant={transactionForm.type === 'expense' ? 'default' : 'outline'}
                  className="flex-1"
                  style={transactionForm.type === 'expense' ? { backgroundColor: theme.accent, color: 'white' } : { borderColor: theme.divider, color: theme.text }}
                >
                  Expense
                </Button>
                <Button
                  onClick={() => setTransactionForm({ ...transactionForm, type: 'income' })}
                  variant={transactionForm.type === 'income' ? 'default' : 'outline'}
                  className="flex-1"
                  style={transactionForm.type === 'income' ? { backgroundColor: theme.income, color: 'white' } : { borderColor: theme.divider, color: theme.text }}
                >
                  Income
                </Button>
              </div>
              <div>
                <Label style={{ color: theme.textSecondary }}>Amount ({currencyInfo.symbol})</Label>
                <Input
                  type="number"
                  value={transactionForm.amount}
                  onChange={(e) => setTransactionForm({ ...transactionForm, amount: e.target.value })}
                  placeholder="500"
                  className="mt-1"
                  style={{ backgroundColor: isDark ? theme.inputBackground : '#FFFFFF', color: theme.text, borderColor: theme.divider }}
                />
              </div>
              <div>
                <Label style={{ color: theme.textSecondary }}>Category</Label>
                <Select 
                  value={transactionForm.category} 
                  onValueChange={(v) => setTransactionForm({ ...transactionForm, category: v })}
                >
                  <SelectTrigger className="mt-1" style={{ backgroundColor: isDark ? theme.inputBackground : '#FFFFFF', color: theme.text, borderColor: theme.divider }}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent style={{ backgroundColor: theme.cardBackground, borderColor: theme.divider }}>
                    {(transactionForm.type === 'income' ? INCOME_CATEGORIES : EXPENSE_CATEGORIES).map(cat => (
                      <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label style={{ color: theme.textSecondary }}>Date</Label>
                <Input
                  type="date"
                  value={transactionForm.date}
                  onChange={(e) => setTransactionForm({ ...transactionForm, date: e.target.value })}
                  className="mt-1"
                  style={{ backgroundColor: isDark ? theme.inputBackground : '#FFFFFF', color: theme.text, borderColor: theme.divider }}
                />
              </div>
              <div>
                <Label style={{ color: theme.textSecondary }}>Description (Optional)</Label>
                <Input
                  value={transactionForm.description}
                  onChange={(e) => setTransactionForm({ ...transactionForm, description: e.target.value })}
                  placeholder="What was this for?"
                  className="mt-1"
                  style={{ backgroundColor: isDark ? theme.inputBackground : '#FFFFFF', color: theme.text, borderColor: theme.divider }}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowTransactionDialog(false)} style={{ borderColor: theme.divider, color: theme.text }}>Cancel</Button>
              <Button onClick={handleAddTransaction} style={{ backgroundColor: theme.accent, color: 'white' }}>
                Add Transaction
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Leave Confirmation */}
        <AlertDialog open={showLeaveDialog} onOpenChange={setShowLeaveDialog}>
          <AlertDialogContent style={{ backgroundColor: theme.cardBackground }}>
            <AlertDialogHeader>
              <AlertDialogTitle style={{ color: theme.text }}>Leave Family</AlertDialogTitle>
              <AlertDialogDescription style={{ color: theme.textSecondary }}>
                Are you sure you want to leave {family.name}? You will lose access to shared budgets and transactions.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleLeaveFamily} style={{ backgroundColor: theme.accent }}>
                Leave
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Delete Confirmation */}
        <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <AlertDialogContent style={{ backgroundColor: theme.cardBackground }}>
            <AlertDialogHeader>
              <AlertDialogTitle style={{ color: theme.text }}>Delete Family</AlertDialogTitle>
              <AlertDialogDescription style={{ color: theme.textSecondary }}>
                Are you sure you want to delete {family.name}? This will remove all members and delete all shared data permanently.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleDeleteFamily} style={{ backgroundColor: '#DC3545' }}>
                Delete Forever
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Remove Member Confirmation */}
        <AlertDialog open={showRemoveDialog} onOpenChange={setShowRemoveDialog}>
          <AlertDialogContent style={{ backgroundColor: theme.cardBackground }}>
            <AlertDialogHeader>
              <AlertDialogTitle style={{ color: theme.text }}>Remove Member</AlertDialogTitle>
              <AlertDialogDescription style={{ color: theme.textSecondary }}>
                Are you sure you want to remove {removingMember?.displayName} from the family?
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={handleRemoveMember} style={{ backgroundColor: theme.accent }}>
                Remove
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
};

export default FamilyBudget;
